import { locale } from '../services/localeService.js';

/**
 * Parse a log entry and transform for display
 * @param {object} log - Raw log entry from API
 * @returns {object} Transformed log entry for display
 */
export function parseLogEntry(log) {
  const before = log.before ? safeJsonParse(log.before) : null;
  const after = log.after ? safeJsonParse(log.after) : null;

  return {
    id: log.id,
    timestamp: log.createdDate,
    userUuid: log.userUuid,
    userEmail: log.user?.email || locale.t('auditlog_system'),
    userName: log.user ? `${log.user.givenName || ''} ${log.user.familyName || ''}`.trim() : locale.t('auditlog_system'),
    changeType: formatChangeType(log.operation),
    entity: formatEntityName(log.modelName, before || after),
    oldValue: getDisplayValue(before, log.operation, log.modelName),
    newValue: getDisplayValue(after, log.operation, log.modelName),
    reason: log.changeReason || null,
    rawBefore: before,
    rawAfter: after,
    operation: log.operation,
    modelName: log.modelName
  };
}

/**
 * Safely parse JSON string
 * @param {string} jsonString - JSON string to parse
 * @returns {object|null} Parsed object or null if parsing fails
 */
function safeJsonParse(jsonString) {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('Failed to parse JSON:', error);
    return null;
  }
}

/**
 * Map operation to display label
 * @param {string} operation - Database operation (CREATE, UPDATE, DELETE)
 * @returns {string} Display label
 */
function formatChangeType(operation) {
  const map = {
    'CREATE': locale.t('auditlog_changetype_created'),
    'UPDATE': locale.t('auditlog_changetype_modified'),
    'DELETE': locale.t('auditlog_changetype_deleted')
  };
  return map[operation] || operation;
}

/**
 * Map model name to display label with context
 * @param {string} modelName - Database model name
 * @param {object} record - The record object (before or after)
 * @returns {string} Display label
 */
function formatEntityName(modelName, record) {
  if (!record) {
    return modelName;
  }

  if (modelName === 'Attribute' && record.key) {
    return `Attribute: ${record.key}`;
  }

  if (modelName === 'Field') {
    // Try to get field name from fieldId or other identifying property
    if (record.fieldId) {
      return `Field: ${record.fieldId}`;
    }
    if (record.fieldType) {
      return `Field: ${record.fieldType}`;
    }
    return 'Field';
  }

  if (modelName === 'Resource') {
    return locale.t('auditlog_entity_item');
  }

  // Default: capitalize model name
  return modelName.charAt(0).toUpperCase() + modelName.slice(1);
}

/**
 * Extract display value from record
 * @param {object} record - The record object
 * @param {string} operation - Database operation (CREATE, UPDATE, DELETE)
 * @param {string} modelName - Database model name
 * @returns {string} Display value
 */
function getDisplayValue(record, operation, modelName) {
  // Handle N/A cases
  if (!record) {
    return locale.t('na');
  }

  // For attribute changes, show the value
  if (modelName === 'Attribute' && record.value !== undefined) {
    return truncateValue(String(record.value), 100);
  }

  // For field changes, try to extract meaningful value
  if (modelName === 'Field') {
    if (record.value !== undefined) {
      return truncateValue(String(record.value), 100);
    }
    if (record.values && Array.isArray(record.values)) {
      return locale.t('auditlog_items_count', { count: record.values.length });
    }
  }

  // For resource changes, show display name or summary
  if (modelName === 'Resource') {
    if (record.displayName) {
      return truncateValue(record.displayName, 100);
    }
    if (record.name) {
      return truncateValue(record.name, 100);
    }
    return locale.t('auditlog_resource_updated');
  }

  // For complex objects, show JSON summary
  const jsonStr = JSON.stringify(record);
  if (jsonStr.length > 100) {
    return truncateValue(jsonStr, 100) + '...';
  }
  return jsonStr;
}

/**
 * Truncate a string value
 * @param {string} value - Value to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated value
 */
function truncateValue(value, maxLength) {
  if (value.length <= maxLength) {
    return value;
  }
  return value.substring(0, maxLength) + '...';
}

function localizeStatus(value) {
  if (value === null || value === undefined || value === '') return locale.t('none');
  const key = 'resourcestatus_' + String(value).toLowerCase();
  const translated = locale.t(key);
  return translated && translated !== key ? translated : value;
}

function localizeAction(value) {
  if (value === null || value === undefined || value === '') return locale.t('none');
  const key = 'resourceaction_' + String(value).toLowerCase();
  const translated = locale.t(key);
  return translated && translated !== key ? translated : value;
}

const IGNORED_DIFF_FIELDS = new Set([
  'createdDate', 'updatedDate', 'sliceId', 'resourceId',
  'mostRecentJobStatus', 'extractDensityIndex', 'fileSize'
]);

const FIELD_LABEL_KEYS = {
  displayName: 'itemname',
  description: 'description',
  externalSourceName: 'externalsource',
  externalSourceId: 'externalid',
  resourceFileName: 'filename',
  mediaType: 'type',
  authoringTool: 'authoringtool',
  launchFile: 'filename',
  status: 'status',
  action: 'action'
};

function listChangedFields(before, after) {
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const labels = [];
  for (const key of keys) {
    if (IGNORED_DIFF_FIELDS.has(key)) continue;
    if ((before?.[key] ?? null) === (after?.[key] ?? null)) continue;
    const labelKey = FIELD_LABEL_KEYS[key];
    const translated = labelKey ? locale.t(labelKey) : null;
    labels.push(translated && translated !== labelKey ? translated : key);
  }
  return labels;
}

/**
 * Format a concise change description for display in the Change column
 * @param {object} log - Parsed log entry
 * @returns {string} Formatted change description (e.g., "Status Changed: Draft → Published")
 */
export function formatChangeDescription(log) {
  const { operation, modelName, rawBefore, rawAfter } = log;

  // RESOURCE operations
  if (modelName === 'Resource') {
    if (operation === 'CREATE') {
      return locale.t('auditlog_action_item_creation');
    }

    if (operation === 'UPDATE' && rawBefore && rawAfter) {
      if (rawBefore.status !== rawAfter.status) {
        return `${locale.t('auditlog_action_status_changed')}: ${localizeStatus(rawBefore.status)} → ${localizeStatus(rawAfter.status)}`;
      }

      if (rawBefore.action !== rawAfter.action) {
        return `${locale.t('auditlog_action_action_changed')}: ${localizeAction(rawBefore.action)} → ${localizeAction(rawAfter.action)}`;
      }

      if (rawBefore.resourceFileName !== rawAfter.resourceFileName) {
        return locale.t('auditlog_action_item_reupload');
      }

      if (rawBefore.displayName !== rawAfter.displayName) {
        const oldName = rawBefore.displayName || locale.t('none');
        const newName = rawAfter.displayName || locale.t('none');
        return `${locale.t('auditlog_action_name_changed')}: ${oldName} → ${newName}`;
      }

      const changedFields = listChangedFields(rawBefore, rawAfter);
      if (changedFields.length > 0) {
        return locale.t('auditlog_action_fields_changed', { fields: changedFields.join(', ') });
      }

      return locale.t('auditlog_action_item_updated');
    }

    if (operation === 'UPLOAD') {
      return locale.t('auditlog_action_uploaded_file');
    }

    if (operation === 'DELETE') {
      return locale.t('auditlog_action_item_deleted');
    }
  }

  // ATTRIBUTE operations
  if (modelName === 'Attribute') {
    const key = rawAfter?.key || rawBefore?.key || 'Unknown';
    const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);

    if (operation === 'CREATE') {
      const newVal = String(rawAfter?.value || '');
      return locale.t('auditlog_attribute_set', { key: capitalizedKey, value: newVal });
    }

    if (operation === 'UPDATE' && rawBefore && rawAfter) {
      const oldVal = rawBefore.value;
      const newVal = rawAfter.value;

      // Check if new value is empty or null (clearing the field)
      if (newVal === null || newVal === undefined || newVal === '') {
        return locale.t('auditlog_attribute_cleared', { key: capitalizedKey, value: String(oldVal) });
      }

      // Check if there was no previous value (null, undefined, or empty string)
      if (oldVal === null || oldVal === undefined || oldVal === '') {
        return locale.t('auditlog_attribute_set', { key: capitalizedKey, value: String(newVal) });
      }

      // There was a previous value, show update
      return locale.t('auditlog_attribute_updated', {
        key: capitalizedKey,
        oldValue: String(oldVal),
        newValue: String(newVal)
      });
    }

    if (operation === 'DELETE') {
      return locale.t('auditlog_attribute_removed', { key: capitalizedKey });
    }
  }

  // NAMING CONVENTION operations (system-attributed; METALARK-1115).
  // `after` carries { value, targetAttributeKey, namingConventionId, trigger }.
  if (modelName === 'NamingConvention') {
    const key = rawAfter?.targetAttributeKey || rawBefore?.targetAttributeKey || '';
    const capitalizedKey = key ? key.charAt(0).toUpperCase() + key.slice(1) : key;
    const value = String(rawAfter?.value ?? '');
    return locale.t('auditlog_computedattributeconvention_set', { key: capitalizedKey, value });
  }

  // FIELD operations
  if (modelName === 'Field') {
    const fieldType = rawAfter?.fieldType || rawBefore?.fieldType || 'Unknown';

    if (operation === 'CREATE') {
      return locale.t('auditlog_field_added', { fieldType });
    }

    if (operation === 'UPDATE') {
      return locale.t('auditlog_field_updated', { fieldType });
    }

    if (operation === 'DELETE') {
      return locale.t('auditlog_field_removed', { fieldType });
    }
  }

  // Fallback
  return log.changeType || locale.t('auditlog_changed');
}

/**
 * Calculate diff between before and after records
 * @param {object} before - Record state before change
 * @param {object} after - Record state after change
 * @returns {Array} Array of changed fields with old/new values
 */
export function calculateDiff(before, after) {
  if (!before || !after) {
    return [];
  }

  const changes = [];
  const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);

  for (const key of allKeys) {
    // Skip metadata fields that aren't user-visible changes
    if (key === 'updatedDate' || key === 'createdDate') {
      continue;
    }

    const beforeValue = before[key];
    const afterValue = after[key];

    // Compare values (handle objects, arrays, primitives)
    if (!isEqual(beforeValue, afterValue)) {
      changes.push({
        field: key,
        oldValue: formatDiffValue(beforeValue),
        newValue: formatDiffValue(afterValue)
      });
    }
  }

  return changes;
}

/**
 * Compare two values for equality (handles objects and arrays)
 * @param {*} a - First value
 * @param {*} b - Second value
 * @returns {boolean} True if values are equal
 */
function isEqual(a, b) {
  // Handle null/undefined
  if (a === b) return true;
  if (a == null || b == null) return false;

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, index) => isEqual(val, b[index]));
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => isEqual(a[key], b[key]));
  }

  // Primitive comparison
  return false;
}

/**
 * Format a value for diff display
 * @param {*} value - Value to format
 * @returns {string} Formatted value
 */
function formatDiffValue(value) {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}
